import { useCallback } from 'react';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AddressType } from '@sweetgreen/garnish';

import type { DeliveryOrderDetailInput } from '../../graphql/types';
import type { AppStackParamList } from '../../navigation/AppNavigation.props';
import { useMenuNavigation } from '../../screens/MenuScreen/MenuScreen.navigation';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

export const useNavigateToMenu = (
  requestedNavigation?: NativeStackNavigationProp<AppStackParamList>,
) => {
  const { handleGoToMenu, handleGoToDeliveryMenu } =
    useMenuNavigation(requestedNavigation);

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({
      orderType,
      restaurantSlug = '',
      deliveryOrderDetail = undefined,
    }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as {
        readonly address: { readonly id: string };
      };
      const addressId =
        deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId);
      }
    },
    [
      inFlightDeliveryOrderId,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

export type UseNavigateToMenuProps = {
  readonly orderType: AddressType;
  readonly restaurantId?: string;
  readonly restaurantSlug?: string;
  readonly restaurantName?: string;
  readonly deliveryAddressName?: string;
  readonly deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
};
